/**
 * Utilitas cetak bersama untuk semua dokumen (voucher, invoice, PO, TTB, BTF).
 *
 * Kenapa tidak langsung `win.print()` setelah `document.write()`:
 * di Chrome, print() yang dipanggil saat dokumen popup belum selesai dimuat &
 * di-layout — terlebih ketika dialog di halaman utama sedang menutup dan
 * merebut fokus kembali — menghasilkan "Print preview failed" / pratinjau
 * kosong. Semua cetakan karena itu lewat `printWhenReady`, yang menunggu
 * dokumen siap, memberi fokus ke popup, baru memanggil print().
 */

const SCRIPT_RE = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
const TOOLBAR_ID = '__print_toolbar__';
const READY_DELAY_MS = 300;
const READY_TIMEOUT_MS = 5000;

export const POPUP_BLOCKED_MESSAGE =
  'Popup diblokir browser. Izinkan popup untuk situs ini lalu coba lagi.';

/**
 * Toolbar "Cetak" & "Tutup" di pojok kanan atas jendela cetak — jalan keluar
 * bila dialog print otomatis gagal atau tertutup. Disisipkan lewat DOM dengan
 * listener dari halaman utama (same-origin), jadi tidak butuh <script>.
 * Disembunyikan saat dicetak lewat @media print.
 */
function addToolbar(win) {
  const doc = win.document;
  if (!doc.body || doc.getElementById(TOOLBAR_ID)) return;

  const style = doc.createElement('style');
  style.textContent = `
    #${TOOLBAR_ID} { position: fixed; top: 10px; right: 10px; z-index: 99999; display: flex; gap: 6px;
      font-family: Arial, sans-serif; }
    #${TOOLBAR_ID} button { font-size: 12px; padding: 6px 12px; border-radius: 6px; cursor: pointer;
      border: 1px solid #94a3b8; background: #fff; color: #0f172a; }
    #${TOOLBAR_ID} button.primary { background: #7e22ce; border-color: #7e22ce; color: #fff; }
    @media print { #${TOOLBAR_ID} { display: none !important; } }
  `;
  (doc.head || doc.body).appendChild(style);

  const bar = doc.createElement('div');
  bar.id = TOOLBAR_ID;
  bar.className = 'no-print';
  bar.setAttribute('data-testid', 'print-toolbar');

  const printBtn = doc.createElement('button');
  printBtn.type = 'button';
  printBtn.className = 'primary';
  printBtn.textContent = 'Cetak';
  printBtn.addEventListener('click', () => { try { win.focus(); win.print(); } catch (e) { /* abaikan */ } });

  const closeBtn = doc.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = 'Tutup';
  closeBtn.addEventListener('click', () => { try { win.close(); } catch (e) { /* abaikan */ } });

  bar.appendChild(printBtn);
  bar.appendChild(closeBtn);
  doc.body.appendChild(bar);
}

/** Tulis HTML ke jendela yang sudah terbuka, lalu pasang toolbar. */
export function writePrintDocument(win, htmlContent) {
  if (!win) return false;
  const doc = win.document;
  doc.open();
  doc.write(String(htmlContent || '').replace(SCRIPT_RE, ''));
  doc.close();
  addToolbar(win);
  return true;
}

/**
 * Panggil print() setelah dokumen siap: readyState complete (atau event load,
 * dengan batas waktu), font termuat, lalu jeda singkat agar layout & dialog di
 * halaman utama selesai menutup. Hanya sekali per jendela.
 */
export function printWhenReady(win) {
  if (!win || win.__printScheduled) return;
  win.__printScheduled = true;

  const fire = () => {
    setTimeout(() => {
      try {
        if (win.closed) return;
        win.focus();
        win.print();
      } catch (e) {
        // Jendela ditutup user di tengah jalan — toolbar "Cetak" tetap ada bila belum.
      }
    }, READY_DELAY_MS);
  };

  const afterLoad = () => {
    let fonts = null;
    try { fonts = win.document.fonts && win.document.fonts.ready; } catch (e) { fonts = null; }
    if (fonts && typeof fonts.then === 'function') {
      let done = false;
      const once = () => { if (!done) { done = true; fire(); } };
      fonts.then(once, once);
      setTimeout(once, READY_TIMEOUT_MS);
    } else {
      fire();
    }
  };

  let readyState = 'complete';
  try { readyState = win.document.readyState; } catch (e) { readyState = 'complete'; }
  if (readyState === 'complete') {
    afterLoad();
  } else {
    let done = false;
    const once = () => { if (!done) { done = true; afterLoad(); } };
    win.addEventListener('load', once, { once: true });
    setTimeout(once, READY_TIMEOUT_MS);
  }
}

/**
 * Buka (atau isi) jendela cetak berisi HTML. Mengembalikan jendela, atau null
 * bila popup diblokir — pemanggil WAJIB memeriksa null sebelum memakainya.
 */
export function openPrintWindow(htmlContent, title = 'Print', existingWindow = null) { // eslint-disable-line no-unused-vars
  // `existingWindow`: jendela yang sudah dibuka sinkron saat klik (lihat
  // openPendingPrintWindow) untuk alur yang menunggu server dulu.
  const win = existingWindow && !existingWindow.closed ? existingWindow : window.open('', '_blank');
  if (!win) return null;
  writePrintDocument(win, htmlContent);
  return win;
}

/** Buka, tulis, cetak. `true` bila berhasil dibuka, `false` bila popup diblokir. */
export function printHtml(htmlContent, title = 'Print') {
  const win = openPrintWindow(htmlContent, title);
  if (!win) return false;
  printWhenReady(win);
  return true;
}

/**
 * Buka jendela cetak SINKRON (di dalam handler klik) untuk alur yang harus
 * menunggu server dulu — window.open() setelah await diblokir browser.
 * Jendela menampilkan pesan tunggu sampai isinya ditulis.
 */
export function openPendingPrintWindow(message = 'Menyiapkan dokumen…') {
  const win = window.open('', '_blank');
  if (!win) return null;
  try {
    const doc = win.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${escapeHtml(message)}</title></head>`
      + `<body style="font-family:Arial,sans-serif;display:flex;align-items:center;justify-content:center;height:90vh;color:#475569">`
      + `<p>${escapeHtml(message)}</p></body></html>`);
    doc.close();
  } catch (e) { /* jendela tetap dipakai */ }
  return win;
}

/**
 * Escape HTML special characters to prevent XSS
 */
export function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Format number to Indonesian locale safely
 */
export function formatRupiah(amount) {
  return (Number(amount) || 0).toLocaleString('id-ID', { maximumFractionDigits: 4 });
}
